import React, { useEffect, useState } from "react";

import { useForm, Controller } from "react-hook-form";
import { GET_TRANSACTION_CHOICES } from "../../graphql/queries/transactionChoices";
import { GET_TOWERS_QUERY } from "../../graphql/queries/towers";
import { useQuery } from "@apollo/client";
import Loader from "../loader/loader";
import hasValue from "../../utils/hasValue";
import DisplaySelect from "../display-select/display-select";
import SingleSelect from "../single-select/single-select";
import MultiSelect from "../multi-select/multi-select";
import stringifyArray from "../../utils/arrayToString";

const TransactionFilters = ({
  onFilter,
  onApplyFilters,
  setAppliedFilters,
  appliedFilters,
}) => {
  const { handleSubmit, control, setValue } = useForm();
  const [gridOptions, setGridOptions] = useState([]);

  // Try to get grids from transactionChoices, but don't fail if it errors
  const {
    loading: choicesLoading,
    error: choicesError,
    data: choicesData,
  } = useQuery(GET_TRANSACTION_CHOICES, {
    fetchPolicy: "cache-and-network",
    errorPolicy: "all", // Continue even if grids field is missing
    skip: false, // Try to fetch it
  });

  // Always fetch towers as fallback/primary source for grids
  const { loading: towersLoading, data: towersData } = useQuery(
    GET_TOWERS_QUERY,
    {
      fetchPolicy: "cache-and-network",
      variables: { first: 1000, skip: 0 },
    }
  );

  useEffect(() => {
    // Use grids from transactionChoices if available, otherwise use towers
    if (
      choicesData?.grids &&
      Array.isArray(choicesData.grids) &&
      choicesData.grids.length > 0
    ) {
      setGridOptions(choicesData.grids);
    } else if (towersData?.towers?.items) {
      const grids = towersData.towers.items.map((tower) => tower.zonkeId);
      setGridOptions(grids);
    }
  }, [choicesData, towersData]);

  const generateOptions = (options) => {
    if (!options || !Array.isArray(options)) return [];
    return options.map((option) => {
      return { value: option, label: option };
    });
  };

  useEffect(() => {
    setValue("success", appliedFilters?.success);
    setValue("transactionPlatform", appliedFilters?.platform);
    setValue("transactionVendor", appliedFilters?.vendor);
    setValue("purchaseType", appliedFilters?.purchaseType);
    setValue("grid", appliedFilters?.grid);
  }, [appliedFilters, setValue]);

  if (choicesLoading || towersLoading) {
    return <Loader sizeClass={"size--medium"} />;
  }

  // Only show error if both queries fail (unlikely)
  if (choicesError && !choicesData && !towersData) {
    return (
      <div className="basic-modal">
        <div className="basic-modal__header">
          <h3>Error Loading Filters</h3>
        </div>
        <div className="basic-modal__body">
          <div
            className="input-container__error"
            style={{
              padding: "20px",
              backgroundColor: "#ffebee",
              border: "1px solid #ef5350",
              borderRadius: "4px",
              color: "#c62828",
            }}
          >
            {choicesError?.message ||
              "Failed to load transaction filter options. Please try again."}
          </div>
          <div className="basic-modal__footer">
            <button onClick={onFilter} className="button button--secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const onSubmit = (data) => {
    setAppliedFilters({
      success: data?.success?.value ? [data.success] : undefined,
      platform: data?.transactionPlatform,
      vendor: data?.transactionVendor,
      purchaseType: data?.purchaseType,
      grid: data?.grid,
    });
    onFilter();
  };

  const displayFilters = (appliedFilters) => {
    const filterStrings = [];

    if (appliedFilters?.success?.length > 0) {
      filterStrings.push(`Success: ${stringifyArray(appliedFilters.success)}`);
    }

    if (appliedFilters?.platform?.length > 0) {
      filterStrings.push(
        `Transaction Platform: ${stringifyArray(appliedFilters.platform)}`
      );
    }

    if (appliedFilters?.vendor?.length > 0) {
      filterStrings.push(`Vendor: ${stringifyArray(appliedFilters.vendor)}`);
    }

    if (appliedFilters?.purchaseType?.length > 0) {
      filterStrings.push(
        `Purchase Type: ${stringifyArray(appliedFilters.purchaseType)}`
      );
    }

    if (appliedFilters?.grid?.length > 0) {
      filterStrings.push(`Grid: ${stringifyArray(appliedFilters.grid)}`);
    }

    return (
      <div className="basic-modal__sub-header-text--bold">
        {filterStrings.join(", ")}
      </div>
    );
  };

  return (
    <div className="basic-modal">
      <div className="basic-modal__header">
        <h3>Filter Transactions</h3>
        {hasValue(appliedFilters) && (
          <div className="basic-modal__sub-header">
            Applied: filters: {displayFilters(appliedFilters)}
          </div>
        )}
      </div>
      <div className="basic-modal__body">
        <form onSubmit={handleSubmit(onSubmit)} className="basic-modal__form">
          <div className="input-container">
            <DisplaySelect placeholder={"Success"} name={"success"} errors={{}}>
              <Controller
                name={"success"}
                rules={{
                  required: false,
                }}
                control={control}
                render={({ field }) => (
                  <SingleSelect
                    field={field}
                    options={[
                      { value: "No", label: "No" },
                      { value: "Yes", label: "Yes" },
                    ]}
                  />
                )}
              />
            </DisplaySelect>
          </div>

          <div className="input-container">
            <DisplaySelect
              placeholder={"Purchase Type"}
              name={"purchaseType"}
              errors={{}}
            >
              <Controller
                name={"purchaseType"}
                control={control}
                render={({ field }) => (
                  <MultiSelect
                    field={field}
                    options={generateOptions(choicesData?.purchaseTypes)}
                  />
                )}
              />
            </DisplaySelect>
          </div>

          <div className="input-container">
            <DisplaySelect
              placeholder={"Transaction Platform"}
              name={"transactionPlatform"}
              errors={{}}
            >
              <Controller
                name={"transactionPlatform"}
                control={control}
                render={({ field }) => (
                  <MultiSelect
                    field={field}
                    options={generateOptions(choicesData?.transactionPlatforms)}
                  />
                )}
              />
            </DisplaySelect>
          </div>
          <div className="input-container">
            <DisplaySelect
              placeholder={"Transaction Vendors"}
              name={"transactionVendor"}
              errors={{}}
            >
              <Controller
                name={"transactionVendor"}
                control={control}
                render={({ field }) => (
                  <MultiSelect
                    field={field}
                    options={generateOptions(choicesData?.transactionVendors)}
                  />
                )}
              />
            </DisplaySelect>
          </div>
          <div className="input-container">
            <DisplaySelect placeholder={"Grid"} name={"grid"} errors={{}}>
              <Controller
                name={"grid"}
                control={control}
                render={({ field }) => (
                  <MultiSelect
                    field={field}
                    options={generateOptions(gridOptions)}
                  />
                )}
              />
            </DisplaySelect>
          </div>
          <div className="basic-modal__footer">
            <button onClick={onFilter} className="button button--secondary">
              Cancel
            </button>
            <button type="submit" className="button button--primary">
              Apply
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TransactionFilters;
